import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
    Loader2, Trash2, MessageSquare, Mail, Phone, Copy, UserPlus, ListTodo, Info, Code, Key,
    ChevronLeft, ChevronRight, Check, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../context/WorkspaceContext';
import { guestMessageAPI } from '../lib/guestMessages';
import { spaceAPI, listAPI } from '../lib/api';

export default function GuestMessagesPage() {
    const { workspaces, currentWorkspace, fetchWorkspaces } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // View Detail modal
    const [detailModal, setDetailModal] = useState({ open: false, row: null });

    // Convert to Contact modal
    const [contactModal, setContactModal] = useState({ open: false, row: null });
    const [contactWsId, setContactWsId] = useState('');
    const [contactSaving, setContactSaving] = useState(false);

    // Convert to Task modal
    const [taskModal, setTaskModal] = useState({ open: false, row: null });
    const [taskSpaces, setTaskSpaces] = useState([]);
    const [taskSpaceId, setTaskSpaceId] = useState('');
    const [taskLists, setTaskLists] = useState([]);
    const [taskListId, setTaskListId] = useState('');
    const [taskLoadingLists, setTaskLoadingLists] = useState(false);
    const [taskSaving, setTaskSaving] = useState(false);

    const load = useCallback(async (p = page) => {
        try {
            setLoading(true);
            const res = await guestMessageAPI.list(p, limit);
            const data = res.data || {};
            const items = Array.isArray(data) ? data : (data.items || data.data || []);
            setRows(items);
            setTotal(data.total ?? items.length);
            const tp = data.total_pages ?? Math.ceil((data.total ?? items.length) / limit);
            setTotalPages(tp || 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal memuat pesan');
        } finally {
            setLoading(false);
        }
    }, [page, limit]);

    useEffect(() => {
        load(page);
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    // Ensure workspaces loaded for dropdowns
    useEffect(() => {
        if (!workspaces || workspaces.length === 0) {
            fetchWorkspaces?.();
        }
    }, [workspaces, fetchWorkspaces]);

    const copyToClipboard = async (text) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Email disalin');
        } catch {
            toast.error('Gagal menyalin');
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Hapus pesan dari "${row.full_name}"?`)) return;
        try {
            await guestMessageAPI.delete(row.id);
            toast.success('Pesan dihapus');
            await load(page);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal menghapus');
        }
    };

    // ---- Convert to Contact ----
    const openContactModal = (row) => {
        setContactWsId(currentWorkspace?.id || '');
        setContactModal({ open: true, row });
    };

    const confirmConvertContact = async () => {
        if (!contactWsId) return toast.error('Pilih workspace terlebih dahulu');
        try {
            setContactSaving(true);
            await guestMessageAPI.convertContact(contactModal.row.id, contactWsId);
            toast.success('Pesan dikonversi menjadi Contact');
            setContactModal({ open: false, row: null });
            await load(page);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal mengonversi');
        } finally {
            setContactSaving(false);
        }
    };

    // ---- Convert to Task ----
    const openTaskModal = (row) => {
        setTaskSpaceId('');
        setTaskListId('');
        setTaskLists([]);
        setTaskSpaces([]);
        setTaskModal({ open: true, row });
        // Preload spaces from current workspace
        const wsId = currentWorkspace?.id;
        if (wsId) {
            spaceAPI.getByWorkspace(wsId).then((res) => setTaskSpaces(res.data || []))
                .catch(() => toast.error('Gagal memuat space'));
        }
    };

    const onTaskSpaceChange = async (val) => {
        setTaskSpaceId(val);
        setTaskListId('');
        setTaskLists([]);
        if (!val) return;
        try {
            setTaskLoadingLists(true);
            const res = await listAPI.getBySpace(val);
            setTaskLists(res.data || []);
        } catch {
            toast.error('Gagal memuat list');
        } finally {
            setTaskLoadingLists(false);
        }
    };

    const confirmConvertTask = async () => {
        if (!taskSpaceId) return toast.error('Pilih space terlebih dahulu');
        if (!taskListId) return toast.error('Pilih list terlebih dahulu');
        try {
            setTaskSaving(true);
            await guestMessageAPI.convertTask(taskModal.row.id, taskSpaceId, taskListId);
            toast.success('Pesan dikonversi menjadi Task');
            setTaskModal({ open: false, row: null });
            await load(page);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal mengonversi');
        } finally {
            setTaskSaving(false);
        }
    };

    const convertedBadge = (row) => {
        const badges = [];
        if (row.converted_contact_id) {
            badges.push(<Badge key="contact" className="bg-green-100 text-green-700 border-green-200">Contact</Badge>);
        }
        if (row.converted_task_id) {
            badges.push(<Badge key="task" className="bg-blue-100 text-blue-700 border-blue-200">Task</Badge>);
        }
        if (badges.length === 0) return null;
        return <div className="flex items-center gap-1">{badges}</div>;
    };

    return (
        <Layout title="Guest Message" subtitle="Pesan dari pihak ketiga melalui website company">
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                </div>
            ) : rows.length === 0 ? (
                <EmptyState />
            ) : (
                <>
                    <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Full Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell className="font-medium">
                                            <div className="flex flex-col">
                                                <span>{r.full_name}</span>
                                                {r.message && (
                                                    <span className="text-xs text-brand-text-muted max-w-xs truncate">{r.message}</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <a href={`mailto:${r.email}`} className="hover:underline">{r.email || '-'}</a>
                                                {r.email && (
                                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(r.email)} title="Salin email">
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{r.phone || '-'}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2 flex-wrap">
                                                <Button variant="ghost" size="icon" onClick={() => setDetailModal({ open: true, row: r })} data-testid={`gm-view-${r.id}`} title="View Detail">
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {convertedBadge(r) && (
                                                    convertedBadge(r)
                                                )}
                                                <Button size="sm" variant="outline" onClick={() => openContactModal(r)} disabled={!!r.converted_contact_id} data-testid={`gm-contact-${r.id}`} title={r.converted_contact_id ? 'Sudah dikonversi ke contact' : 'Convert to Contact'}>
                                                    <UserPlus className="w-3.5 h-3.5 mr-1" /> Contact
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => openTaskModal(r)} disabled={!!r.converted_task_id} data-testid={`gm-task-${r.id}`} title={r.converted_task_id ? 'Sudah dikonversi ke task' : 'Convert to Task'}>
                                                    <ListTodo className="w-3.5 h-3.5 mr-1" /> Task
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(r)} data-testid={`gm-delete-${r.id}`} title="Hapus">
                                                    <Trash2 className="w-4 h-4 text-red-600" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <span className="text-sm text-brand-text-muted">
                                Total: {total} pesan
                            </span>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="text-sm">Hal {page} / {totalPages}</span>
                                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Convert to Contact Modal */}
            <Dialog open={contactModal.open} onOpenChange={(o) => setContactModal({ open: o, row: o ? contactModal.row : null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Convert to Contact</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-brand-text-muted">
                            Konversi pesan dari <strong>{contactModal.row?.full_name}</strong> menjadi Contact CRM.
                        </p>
                        <div>
                            <Label>Pilih Workspace *</Label>
                            <Select value={contactWsId} onValueChange={setContactWsId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Pilih workspace" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(workspaces || []).map((ws) => (
                                        <SelectItem key={ws.id} value={ws.id}>{ws.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setContactModal({ open: false, row: null })}>Batal</Button>
                        <Button onClick={confirmConvertContact} disabled={contactSaving} data-testid="gm-contact-confirm">
                            {contactSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Konfirmasi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Convert to Task Modal */}
            <Dialog open={taskModal.open} onOpenChange={(o) => setTaskModal({ open: o, row: o ? taskModal.row : null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Convert to Task</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-brand-text-muted">
                            Konversi pesan dari <strong>{taskModal.row?.full_name}</strong> menjadi Task.
                        </p>
                        <div>
                            <Label>Pilih Space *</Label>
                            <Select value={taskSpaceId} onValueChange={onTaskSpaceChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Pilih space" />
                                </SelectTrigger>
                                <SelectContent>
                                    {taskSpaces.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Pilih List *</Label>
                            <Select value={taskListId} onValueChange={setTaskListId} disabled={!taskSpaceId || taskLoadingLists}>
                                <SelectTrigger>
                                    <SelectValue placeholder={taskLoadingLists ? 'Memuat...' : 'Pilih list'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {taskLists.map((l) => (
                                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTaskModal({ open: false, row: null })}>Batal</Button>
                        <Button onClick={confirmConvertTask} disabled={taskSaving} data-testid="gm-task-confirm">
                            {taskSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Konfirmasi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Detail Modal */}
            <Dialog open={detailModal.open} onOpenChange={(o) => setDetailModal({ open: o, row: o ? detailModal.row : null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Detail Guest Message</DialogTitle>
                    </DialogHeader>
                    {detailModal.row && (
                        <div className="space-y-4 mt-2">
                            <div className="grid grid-cols-[120px_1fr] gap-3 text-sm">
                                <span className="text-brand-text-muted font-medium">Full Name</span>
                                <span>{detailModal.row.full_name || '-'}</span>

                                <span className="text-brand-text-muted font-medium">Email</span>
                                <div className="flex items-center gap-1">
                                    <a href={`mailto:${detailModal.row.email}`} className="hover:underline">{detailModal.row.email || '-'}</a>
                                    {detailModal.row.email && (
                                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(detailModal.row.email)} title="Salin email">
                                            <Copy className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>

                                <span className="text-brand-text-muted font-medium">Phone</span>
                                <span>{detailModal.row.phone || '-'}</span>

                                <span className="text-brand-text-muted font-medium">Status</span>
                                <span>{convertedBadge(detailModal.row) || <Badge className="bg-gray-100 text-gray-700 border-gray-200">New</Badge>}</span>

                                <span className="text-brand-text-muted font-medium">Created At</span>
                                <span>{detailModal.row.created_at ? new Date(detailModal.row.created_at).toLocaleString('id-ID') : '-'}</span>
                            </div>

                            <div>
                                <Label className="text-sm font-semibold">Message</Label>
                                <div className="mt-1 p-3 bg-gray-50 border border-brand-border rounded-lg text-sm whitespace-pre-wrap">
                                    {detailModal.row.message || '(tidak ada pesan)'}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetailModal({ open: false, row: null })}>Tutup</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function EmptyState() {
    return (
        <div className="space-y-6">
            {/* Info card */}
            <div className="bg-white border border-brand-border rounded-lg p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-accent-soft flex items-center justify-center flex-shrink-0">
                        <Info className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">Belum ada pesan</h3>
                        <p className="text-sm text-brand-text-muted mt-1">
                            Menu ini menampilkan pesan yang dikirim dari website company Anda melalui API.
                        </p>
                    </div>
                </div>
            </div>

            {/* API documentation */}
            <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-6 py-4 border-b border-brand-border">
                    <Code className="w-5 h-5 text-brand-primary" />
                    <h3 className="font-semibold">API Documentation</h3>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <Label className="text-sm font-semibold">Endpoint</Label>
                        <p className="text-sm text-brand-text-muted mt-1">
                            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">POST /api/third-party/guest-messages</code>
                        </p>
                    </div>

                    <div>
                        <Label className="text-sm font-semibold">Required Fields</Label>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Field</TableHead>
                                    <TableHead>Type</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow><TableCell className="font-mono text-xs">full_name</TableCell><TableCell>string</TableCell></TableRow>
                                <TableRow><TableCell className="font-mono text-xs">email</TableCell><TableCell>string</TableCell></TableRow>
                                <TableRow><TableCell className="font-mono text-xs">phone</TableCell><TableCell>string</TableCell></TableRow>
                                <TableRow><TableCell className="font-mono text-xs">message</TableCell><TableCell>string</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </div>

                    <div>
                        <Label className="text-sm font-semibold">Example (curl)</Label>
                        <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto mt-1"><code>{`curl -X POST "https://api.example.com/api/third-party/guest-messages" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"full_name": "John Doe", "email": "john@example.com", "phone": "+1234567890", "message": "Hello, I am interested in your services."}'`}</code></pre>
                    </div>

                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <Key className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">
                            Anda memerlukan Third-Party API Key. Hubungi SuperAdmin untuk mendapatkan API key dengan permission <code className="font-mono bg-amber-100 px-1 rounded">write:guest_messages</code>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}